import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comicCategories, hotComics, latestUpdates, completedComics } from '@/mocks/comics';
import { WebSiteSchema, WebPageSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';

type FilterType = 'all' | 'hot' | 'latest' | 'completed';

const allComics = [...hotComics, ...latestUpdates, ...completedComics];

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('type') || 'all');
  const [activeFilter, setActiveFilter] = useState<FilterType>((searchParams.get('filter') as FilterType) || 'all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory, activeFilter]);

  const updateParams = (type: string, filter: FilterType, search: string) => {
    const params: Record<string, string> = {};
    if (type && type !== 'all') params.type = type;
    if (filter && filter !== 'all') params.filter = filter;
    if (search) params.search = search;
    setSearchParams(params);
  };

  const handleCategoryChange = (slug: string) => {
    setActiveCategory(slug);
    updateParams(slug, activeFilter, searchQuery);
  };

  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter);
    updateParams(activeCategory, filter, searchQuery);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateParams(activeCategory, activeFilter, searchQuery);
  };

  // Filter comics
  let filteredComics = allComics;

  if (activeFilter === 'hot') {
    filteredComics = [...hotComics];
  } else if (activeFilter === 'latest') {
    filteredComics = [...latestUpdates];
  } else if (activeFilter === 'completed') {
    filteredComics = [...completedComics];
  } else {
    // Deduplicate by id
    const seen = new Set<string>();
    filteredComics = allComics.filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }

  if (activeCategory !== 'all') {
    filteredComics = filteredComics.filter((c) =>
      c.categories.some((cat) => {
        const catSlug = comicCategories.find((cc) => cc.name === cat)?.slug;
        return catSlug === activeCategory;
      })
    );
  }

  if (searchQuery.trim()) {
    const q = searchQuery.trim().toLowerCase();
    filteredComics = filteredComics.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.author.toLowerCase().includes(q) ||
        c.tags.some((t) => t.toLowerCase().includes(q))
    );
  }

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const totalPages = Math.ceil(filteredComics.length / itemsPerPage);
  const paginatedComics = filteredComics.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, activeFilter, searchQuery]);

  const activeCatName = comicCategories.find((c) => c.slug === activeCategory)?.name || '全部';

  const filterTabs: { key: FilterType; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'hot', label: '热门' },
    { key: 'latest', label: '最新' },
    { key: 'completed', label: '完结' },
  ];

  return (
    <>
      <WebSiteSchema />
      <WebPageSchema
        title={`${activeCategory !== 'all' ? activeCatName + '漫画' : '漫画分类'} | 奇妙漫画`}
        description="探索海量漫画资源，按类型快速筛选你喜欢的作品。韩漫、日漫、国漫，热血、恋爱、奇幻等多种类型任你选择。"
        url={`https://www.qimiaomanhua.com/category${activeCategory !== 'all' ? '?type=' + activeCategory : ''}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://www.qimiaomanhua.com' },
          { name: '漫画分类', url: 'https://www.qimiaomanhua.com/category' },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        {/* Page Header */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600">漫画分类</span>
              {activeCategory !== 'all' && (
                <>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-foreground-800 font-medium">{activeCatName}</span>
                </>
              )}
            </nav>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {activeCategory !== 'all' ? `${activeCatName}漫画` : '漫画分类'}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              {activeCategory !== 'all'
                ? `浏览所有${activeCatName}类漫画作品，发现你的下一部心头好`
                : '探索海量漫画资源，按类型快速筛选你喜欢的作品'}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="w-full px-4 md:px-6 py-6 bg-background-50 border-b border-background-100 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => handleCategoryChange('all')}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeCategory === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {comicCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Filter + Search Bar */}
        <section className="w-full px-4 md:px-6 py-4 bg-background-50">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              {filterTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleFilterChange(tab.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeFilter === tab.key
                      ? 'bg-secondary-500 text-white'
                      : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <form onSubmit={handleSearch} className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-56">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索漫画名称或作者..."
                  className="w-full h-9 pl-4 pr-10 rounded-full text-sm bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
                <button
                  type="submit"
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full text-foreground-500 hover:text-primary-500"
                >
                  <i className="ri-search-line text-sm"></i>
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* Results */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50 min-h-[400px]">
          <div className="max-w-7xl mx-auto">
            {paginatedComics.length > 0 ? (
              <>
                <p className="text-xs text-foreground-400 mb-6">
                  共找到 <strong className="text-foreground-700">{filteredComics.length}</strong> 部漫画
                  {searchQuery && <span>，搜索关键词：<strong className="text-foreground-700">"{searchQuery}"</strong></span>}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                  {paginatedComics.map((comic) => (
                    <Link
                      key={`${comic.id}-${activeCategory}-${activeFilter}`}
                      to={`/comic/${comic.id}`}
                      className="group relative bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-200 hover:-translate-y-1"
                    >
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                          src={comic.coverImage}
                          alt={comic.title}
                          title={`${comic.title} - 奇妙漫画${comic.categories.join('·')}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                        {comic.status === '已完结' && (
                          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-semibold">
                            完结
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <h3 className="font-heading font-semibold text-foreground-900 text-sm line-clamp-1 mb-1">
                          {comic.title}
                        </h3>
                        <p className="text-xs text-foreground-400 line-clamp-1 mb-2">{comic.author}</p>
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1 text-xs text-amber-500">
                            <i className="ri-star-fill text-[10px]"></i>
                            {comic.rating}
                          </span>
                          <span className="text-[10px] text-foreground-400">{comic.chapters.length}话</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                          currentPage === i + 1
                            ? 'bg-primary-500 text-white'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-700 mb-2">
                  暂无匹配的漫画
                </h3>
                <p className="text-sm text-foreground-400 max-w-sm">
                  {searchQuery
                    ? `没有找到与"${searchQuery}"相关的漫画，试试其他关键词吧`
                    : '该分类下暂时没有漫画，请切换其他分类'}
                </p>
                <button
                  onClick={() => {
                    setActiveCategory('all');
                    setActiveFilter('all');
                    setSearchQuery('');
                    updateParams('all', 'all', '');
                  }}
                  className="mt-4 px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors whitespace-nowrap"
                >
                  查看全部漫画
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}